/**
 * The editor, assembled.
 *
 * The tool rail, one side panel when asked for, the page with its floating
 * toolbar, and one warning strip - Canva's arrangement, §6.2. This file does
 * the arranging and the two things that do not belong to any one panel:
 * opening a design, and saving it without the customer having to think about it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, blankDocument, getConfig, signedIn } from './api';
import type { Template } from './api';
import type { Design } from './model/types';
import { dropDraft, forgetPicture, keptPictures, localIds, readDraft, takePending, unsavedDesign, type Draft, type Pending } from './local';
import { commitForBasket, currentDraftKey, ensureStored, forgetResume, keepLocally, moveIntoAccount } from './state/persist';
import { BasketProgress, CheckDialog, LeftoverNotice, SignInDialog, SignedOutDesigns } from './ui/Account';
import { registerFonts, preload } from './text/fonts';
import { useEditor } from './state/store';
import { useShortcuts } from './state/shortcuts';
import { addImage, firstFamily } from './state/actions';
import { registerAssets } from './assets';
import { flatten } from './model/types';
import type { TrimboxDocument } from './model/types';
import { CanvasView } from './canvas/CanvasView';
import { TopBar } from './ui/TopBar';
import { ToolRail, type RailPanel } from './ui/ToolRail';
import { DesignPanel, DrawPanel, ElementsPanel, TextPanel, UploadsPanel } from './ui/RailPanels';
import { ContextBar, type ColourTarget, type Panel } from './ui/ContextBar';
import { EffectsPanel } from './ui/EffectsPanel';
import { ColourPanel, FontPanel, PositionPanel, SettingsPanel } from './ui/Panels';
import { PreflightBar, check } from './ui/PreflightBar';
import { CanvasFoot } from './ui/CanvasFoot';
import { PageStrip } from './ui/PageStrip';
import { Shortcuts } from './ui/Shortcuts';
import { LayersPanel } from './ui/LayersPanel';
import { MaskPanel } from './ui/MaskPanel';
import { ThreeView, isPhone } from './ui/ThreeView';
import { LayerMenu, type MenuAt } from './ui/LayerMenu';
import { DesignsPanel } from './ui/DesignsPanel';
import { Templates } from './ui/Templates';
import { ImportDialog } from './ui/ImportDialog';
import './styles/tokens.css';
import './styles/app.css';
import './styles/toolbar.css';
import './styles/chrome.css';

/** How long after the last change an autosave goes out. */
const AUTOSAVE_MS = 2500;

/** The panels the floating toolbar opens, as against the rail's. */
const TOOLBAR_PANELS: Panel[] = ['effects', 'position', 'settings', 'font', 'colour', 'mask'];

/** Whatever is in the side column. */
type Side = RailPanel | Panel;

export function App() {
  const config = getConfig();


  const open = useEditor((s) => s.open);
  const doc = useEditor((s) => s.doc);
  const dirty = useEditor((s) => s.dirty);
  const markSaved = useEditor((s) => s.markSaved);

  /*
   * The side column: one panel at a time, whichever opened it - the rail
   * (Design, Elements, Text, ...) or the floating toolbar (Effects, Position,
   * ...). One value, so two panels can never be drawn at once.
   */
  const [side, setSide] = useState<Side | null>(null);
  /*
   * 3D opens by itself on a product that has it - it is the thing the customer
   * came to see, and a panel behind a button is a panel most people never open.
   *
   * Not on a phone: there the 3D view is the whole screen, so opening it by
   * default would put the customer in front of a blank product with their
   * design one tap away, rather than in front of their design.
   */
  const [three, setThree] = useState(() => {
    if (!config.three) return false;

    // A phone gets the whole screen either way, so never on opening there.
    if (isPhone()) return false;

    /*
     * Closed stays closed, in this browser, until they open it again. The
     * divider's position is already remembered the same way - a customer who
     * does not want the preview should not have to dismiss it every visit.
     */
    try {
      const kept = window.localStorage.getItem('trimbox.three.open');

      if (kept !== null) return '1' === kept;
    } catch {
      // Private window, or site data blocked. Open, as it would be anyway.
    }

    return true;
  });

  // Written on every change rather than only on closing, so reopening it is
  // remembered too.
  useEffect(() => {
    if (!config.three) return;

    try {
      window.localStorage.setItem('trimbox.three.open', three ? '1' : '0');
    } catch {
      // Nothing to do: the preview still works, it is just not remembered.
    }
  }, [three, config.three]);
  /*
   * Whether the preview takes half the grid, rather than covering the screen.
   *
   * A phone gets the whole screen, so there is no split there and no column to
   * keep open behind it. Read once, like ThreeView does: a phone does not
   * become a desktop mid-design.
   */
  const phone = useRef(isPhone());
  const splitScreen = three && !!config.three && !phone.current;

  const [colourFor, setColourFor] = useState<ColourTarget>('fill');

  /** Drawing with the pen or the pencil, or neither. */
  const [mode, setMode] = useState<'select' | 'pen' | 'pencil'>('select');

  const toolbarPanel = side && (TOOLBAR_PANELS as string[]).includes(side) ? (side as Panel) : null;

  const openPanel = useCallback((next: Panel, target?: ColourTarget) => {
    if (target) setColourFor(target);

    // The same button again closes it - unless it is the colour panel being
    // asked for a different colour, which switches instead.
    setSide((current) => (current === next && (!target || next !== 'colour') ? null : next));
  }, []);

  const openRail = useCallback((next: RailPanel) => {
    setSide((current) => (current === next ? null : next));
  }, []);

  const draw = useCallback((which: 'pen' | 'pencil') => {
    // Out of any field first - the Elements search, say - or Escape and Enter
    // would go to the field as typing, and the pen would not hear them.
    if (document.activeElement instanceof HTMLElement && document.activeElement.matches('input, textarea, select')) {
      document.activeElement.blur();
    }

    setMode((current) => (current === which ? 'select' : which));
  }, []);

  // A panel about the selection has nothing to show once there is none. The
  // page's own panels - its settings, its background - are exactly what is
  // wanted then, and stay; so do the rail's.
  const selectionCount = useEditor((s) => s.selection.length);
  const colourForRef = useRef(colourFor);

  colourForRef.current = colourFor;

  useEffect(() => {
    if (selectionCount !== 0) return;

    setSide((current) => {
      if (!current || !(TOOLBAR_PANELS as string[]).includes(current)) return current;

      return current === 'settings' || (current === 'colour' && colourForRef.current === 'page') ? current : null;
    });
  }, [selectionCount]);
  const [menu, setMenu] = useState<MenuAt | null>(null);

  /*
   * A panel the canvas asked for - double-clicking a QR code, which has nothing
   * to edit in place and everything to edit in a panel.
   */
  const wantPanel = useEditor((s) => s.wantPanel);

  useEffect(() => {
    if (!wantPanel) return;

    setSide(wantPanel as Side);
    useEditor.getState().panelAsked();
  }, [wantPanel]);

  /*
   * The template gallery: 'greeting' is the one offered on a blank card, and
   * 'asked' is the one reached from the Designs panel afterwards. They differ
   * only in wording and in what closing them means.
   */
  const [templates, setTemplates] = useState<'greeting' | 'asked' | null>(null);

  /*
   * Importing someone else's artwork onto a template. The shop's tool, so it
   * is only ever opened from the File menu while a template is being drawn.
   */
  const [importing, setImporting] = useState(false);

  /*
   * The shop's templates for this product, fetched once on opening.
   *
   * Held here rather than in the gallery so that nothing is shown until there
   * is something to show - a sheet that appears while it decides whether it has
   * anything covers the canvas and eats the customer's first click.
   */
  const [offers, setOffers] = useState<Template[]>([]);
  const [status, setStatus] = useState<'opening' | 'ready' | 'failed'>('opening');
  const [problem, setProblem] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  /** Why the sign-in dialog is up, if it is. */
  const [asking, setAsking] = useState<Pending | null>(null);
  /** What to do once the design has moved into the account after signing in. */
  const [after, setAfter] = useState<Pending | null>(null);
  /** A design from before signing in, found on this card and not yet taken. */
  const [leftover, setLeftover] = useState<Draft | null>(null);
  /** A visitor's work is on its way into their account. */
  const [moving, setMoving] = useState(false);
  /** Good news, said once: a toast that is not a problem. */
  const [notice, setNotice] = useState('');
  /** The shop's "have you checked it?" box, on the way to the basket. */
  const [checking, setChecking] = useState(false);
  /**
   * How far the basket is along, while it is happening.
   *
   * Deliberately not `saving`: that one is the quiet autosave indicator and is
   * cleared the moment the request comes back. This survives until the page
   * itself goes, because the two server round-trips after the save - adding
   * the line, then the redirect to the basket - are most of the wait.
   */
  const [sending, setSending] = useState<'saving' | 'adding' | null>(null);

  const fitRef = useRef<() => void>(() => {});
  const thumbRef = useRef<() => string | null>(() => null);

  // Hoisted above the early returns below - a hook that only sometimes runs is
  // a hook that will eventually run in the wrong order.
  const captureFit = useCallback((fit: () => void) => {
    fitRef.current = fit;
  }, []);

  const captureThumb = useCallback((take: () => string | null) => {
    thumbRef.current = take;
  }, []);

  /* --- opening ------------------------------------------------- */

  useEffect(() => {
    let cancelled = false;

    // The page carries the library grouped; the registry wants one record per
    // face, so it is unpacked here rather than shipped that way.
    registerFonts(
      config.fonts.flatMap((group) =>
        group.faces.map(([file, weight, italic]) => ({
          family: group.family,
          weight,
          style: italic ? ('italic' as const) : ('normal' as const),
          url: group.url + file,
        }))
      )
    );

    (async () => {
      try {
        const existing = await opening();

        if (cancelled) return;

        open(existing);
        setStatus('ready');

        // Signed picture links expire, so they are fetched on opening rather
        // than stored in the document.
        if (existing.uuid) {
          void api
            .assets(existing.uuid)
            .then(registerAssets)
            .catch(() => setProblem('Some pictures in this design could not be loaded.'));
        }

        // And a visitor's pictures are in their own browser.
        void keptPictures(existing.document).then(registerAssets);

        // Fonts after the document, not before: the shell should appear while
        // HarfBuzz and the faces are still arriving. §18.
        /*
         * What the design uses, and failing that the one face a new text layer
         * will ask for.
         *
         * This used to fall back to every face the shop offers, which was fine
         * when the shop offered two and is half a gigabyte when it offers a
         * real library. But it must not fall to nothing either: the first face
         * is what warms HarfBuzz, and without it the wait for the shaper lands
         * on the customer the moment they add their first word.
         */
        const used = existing.document.fonts.length
          ? existing.document.fonts
          : firstFaces(existing.document);

        void preload(used.length ? used : [defaultFace()]);

        /*
         * Templates are offered only on a card with nothing on it, and only
         * when the product actually has some. A template replaces the whole
         * document, so offering one over work already done is a way to lose an
         * afternoon; opening a saved design goes straight to the canvas. And
         * never to the shop drawing a template of its own.
         */
        void api
          .templates(config.product.id, config.product.variation)
          .then((list) => {
            if (cancelled || list.length === 0) return;

            setOffers(list);

            if (isBlank(existing.document) && !config.template) setTemplates('greeting');
          })
          .catch(() => undefined);
      } catch (error) {
        if (cancelled) return;

        setProblem(error instanceof Error ? error.message : 'The design could not be opened.');
        setStatus('failed');
      }
    })();

    /**
     * Which design to open, and from where.
     *
     *   a uuid            A stored design - the customer's, or a template.
     *   a visitor         Their draft in this browser, or a blank card.
     *   back from sign-in The draft moves into the account, and whatever they
     *                     were doing when asked to sign in is picked up again.
     *   signed in         A blank card, not yet stored - see ensureStored(). A
     *                     draft left from before an ordinary sign-in is offered,
     *                     not taken: on a shared computer it may be somebody else's.
     */
    async function opening(): Promise<Design> {
      if (config.uuid) return api.get(config.uuid);

      const key = currentDraftKey();
      const draft = readDraft(key);
      const resume = new URLSearchParams(window.location.search).has('resume');

      forgetResume();

      if (!signedIn()) {
        return draft
          ? unsavedDesign({ ...draft.doc, product: { id: config.product.id, variation: config.product.variation } }, draft.title)
          : unsavedDesign(blankDocument(config));
      }

      if (draft && resume) {
        // Read before the move, which clears the browser's copy - reason and all.
        const then = takePending(key);

        try {
          setMoving(true);

          const moved = await moveIntoAccount(draft);

          setAfter(then);

          return moved;
        } catch (error) {
          // Still in the browser; offered again below rather than lost.
          setProblem(
            `We could not move your design into your account yet: ${error instanceof Error ? error.message : 'please try again.'}`
          );
        } finally {
          setMoving(false);
        }
      }

      if (draft) setLeftover(draft);

      return unsavedDesign(blankDocument(config));
    }

    return () => {
      cancelled = true;
    };
  }, [config, open]);

  /* --- saving -------------------------------------------------- */

  /**
   * Keeps the design: in the account for a customer, in the browser for a
   * visitor. One at a time - a deliberate save pressed during an autosave
   * waits for it rather than being dropped.
   */
  const persist = useCallback(
    async (options: { version?: boolean | string } = {}) => {
      const state = useEditor.getState();

      if (!state.design || !state.doc) return;

      /*
       * A visitor's work in progress stays in their own browser. Once it has
       * been put in a basket it exists on the server - a guest's order has to
       * print from something - and from then on saving means saving there, or
       * "Change it" at the checkout would change nothing.
       */
      if (!signedIn() && !state.design.uuid) {
        if (keepLocally()) {
          if (useEditor.getState().doc === state.doc) markSaved();
          setSavedAt(Date.now());
        } else {
          setProblem('Your browser would not keep this design. Sign in to save it to your account.');
        }

        return;
      }

      // Nothing changed and nothing asked for: an autosave with no work to do.
      if (state.design.uuid && !state.dirty && !options.version) return;

      setSaving(true);

      try {
        const had = state.design.uuid;
        const uuid = had || (await ensureStored());

        // A design created just now was created with this document; sending
        // it straight back would only file an identical version.
        if (had) await api.save(uuid, state.doc, options);

        // Only on a deliberate save, not on every autosave: a thumbnail is
        // for the basket, and re-encoding the card every two seconds while
        // someone is still moving things around buys nothing.
        if (options.version) {
          const picture = thumbRef.current();

          if (picture) await api.thumb(uuid, picture).catch(() => undefined);

          // The product in 3D beside it, when this product shows 3D (PLAN-3D.md §8).
          // A picture that fails is left out; the save itself has already worked.
          if (config.three && picture) {
            const { snapshot3d, pair } = await import('./three/snapshot');
            const shot = await snapshot3d(config.three, state.doc);

            if (shot) {
              await api.thumb(uuid, shot, '3d').catch(() => undefined);
              await api.thumb(uuid, await pair(picture, shot), 'pair').catch(() => undefined);
            }
          } else if (!config.three && state.design.thumb3d) {
            // 3D has been switched off for this product since: the old picture goes.
            await api.thumb(uuid, '', '3d').catch(() => undefined);
          }
        }

        // Only clear the flag if nothing changed while the request was out -
        // otherwise an edit made mid-save is silently marked as saved.
        if (useEditor.getState().doc === state.doc) markSaved();

        setSavedAt(Date.now());
      } catch (error) {
        setProblem(error instanceof Error ? error.message : 'Your design could not be saved.');
      } finally {
        setSaving(false);
      }
    },
    [markSaved]
  );

  const queue = useRef<Promise<void>>(Promise.resolve());

  const save = useCallback(
    (options: { version?: boolean | string } = {}) => {
      const next = queue.current.then(() => persist(options));

      queue.current = next.catch(() => undefined);

      return next;
    },
    [persist]
  );

  /** What Save means: to a customer, save; to a visitor, sign in first. */
  const saveDeliberately = useCallback(() => {
    if (signedIn()) void save({ version: true });
    else setAsking('save');
  }, [save]);

  /**
   * Off to the product page with the design, saved and pictured first - the
   * basket line shows the thumbnail, and the order has to be the design as it
   * is on screen now rather than as the last autosave left it.
   */
  const addToBasket = useCallback(
    async (checked = false) => {
      // What the shop asks before it prints: the customer's own eyes on their
      // own card. Asked once, here, where the card is still on screen.
      if (config.confirm && !checked) {
        setChecking(true);

        return;
      }

      /*
       * No account needed to buy.
       *
       * A visitor has been designing in their own browser; this is the moment
       * that work becomes an order, so it - and every picture in it - goes to
       * the shop now. A signed-in customer's is already there, and this costs
       * them one save.
       */
      setSaving(true);
      setSending('saving');

      let uuid = '';

      try {
        uuid = await commitForBasket();

        await save({ version: true });
      } catch (error) {
        setProblem(
          error instanceof Error ? error.message : 'Your design could not be sent to the shop. Please try again.'
        );

        // Only here. On the way out the overlay stays up until the basket
        // replaces the page - see below.
        setSaving(false);
        setSending(null);

        return;
      }

      setSaving(false);

      if (!uuid) {
        setSending(null);

        return;
      }

      /*
       * Straight to the basket, by way of the product.
       *
       * The address is the product's, and trimbox_add asks the shop to put the
       * line in and send them on to the basket - carrying the variation the
       * card was designed at, because that variation is the price. If the shop
       * cannot be certain of it, it leaves them on the product page instead,
       * which is where they used to land anyway.
       */
      const to = new URL(config.addToCartUrl, window.location.href);

      to.searchParams.set('trimbox', uuid);
      to.searchParams.set('trimbox_add', '1');

      if (config.product.variation) to.searchParams.set('variation', String(config.product.variation));

      if (checked) to.searchParams.set('trimbox_confirmed', '1');

      /*
       * The design is away; what is left is the shop's two redirects. Nothing
       * on this page can fail from here, and the overlay is deliberately never
       * taken down - the basket arriving is what removes it.
       */
      setSending('adding');

      window.location.href = to.toString();
    },
    [save, config.addToCartUrl, config.confirm]
  );

  /**
   * A proof to keep: the stamped PDF, made on the server and opened.
   *
   * Saved first, because a proof of a design the shop has not got is a proof of
   * nothing - and never the press file, which is what the shop is being paid
   * to produce.
   */
  const downloadProof = useCallback(async () => {
    if (!signedIn()) {
      setAsking('proof');

      return;
    }

    setNotice('Making your proof…');

    try {
      await save({ version: true });

      const uuid = useEditor.getState().design?.uuid;

      if (!uuid) return;

      const { link } = await api.proof(uuid);

      setNotice('');
      window.open(link, '_blank', 'noopener');
    } catch (error) {
      setNotice('');
      setProblem(error instanceof Error ? error.message : 'That proof could not be made.');
    }
  }, [save]);

  /** The shop has finished a template: saved, pictured, and back to its screen. */
  const finishTemplate = useCallback(async () => {
    await save({ version: true });

    if (config.template && !useEditor.getState().dirty) window.location.href = config.template.editUrl;
  }, [save, config.template]);

  /*
   * What the customer was doing when they were asked to sign in, done now that
   * they have. After the first paint, so the thumbnail has a canvas to read.
   */
  useEffect(() => {
    if (status !== 'ready' || !after) return;

    const timer = window.setTimeout(() => {
      const what = after;

      setAfter(null);

      if (what === 'save') void save({ version: true }).then(() => setNotice('Saved to your account.'));
      if (what === 'basket') void addToBasket();
      if (what === 'proof') void downloadProof();
      if (what === 'designs') openRail('projects');
    }, 400);

    return () => window.clearTimeout(timer);
  }, [status, after, save, addToBasket, downloadProof, openRail]);

  /** The leftover draft, taken into the account after all. */
  const keepLeftover = useCallback(async () => {
    if (!leftover) return;

    setMoving(true);

    try {
      const moved = await moveIntoAccount(leftover);

      open(moved);
      void keptPictures(moved.document).then(registerAssets);
      setLeftover(null);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'That design could not be moved into your account.');
    } finally {
      setMoving(false);
    }
  }, [leftover, open]);

  const discardLeftover = useCallback(() => {
    if (!leftover) return;

    for (const id of localIds(leftover.doc)) void forgetPicture(id);

    dropDraft(currentDraftKey());
    setLeftover(null);
  }, [leftover]);

  // Autosave: quiet, debounced, and never blocking input. §6.4.
  useEffect(() => {
    if (!dirty || status !== 'ready') return;

    const timer = window.setTimeout(() => void save(), AUTOSAVE_MS);

    return () => window.clearTimeout(timer);
  }, [dirty, doc, status, save]);

  /*
   * Coming back from the basket with the Back button.
   *
   * The browser restores this page exactly as it left it - including the
   * overlay, which was never taken down because the page was on its way out.
   * Without this the customer returns to a frozen "Adding it to your basket".
   */
  useEffect(() => {
    const back = (event: PageTransitionEvent) => {
      if (event.persisted) setSending(null);
    };

    window.addEventListener('pageshow', back);

    return () => window.removeEventListener('pageshow', back);
  }, []);

  // Leaving with unsaved work is worth one interruption.
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!useEditor.getState().dirty) return;

      // A visitor's work goes into their browser on the way out, so there is
      // nothing to warn them about - it will be here when they come back.
      if (!signedIn() && keepLocally()) return;

      event.preventDefault();
    };

    window.addEventListener('beforeunload', warn);

    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  /* --- tools --------------------------------------------------- */

  // The browser's own file picker. A custom one would be prettier and would
  // not reach a phone's camera roll.
  const picker = useRef<HTMLInputElement>(null);

  useShortcuts({
    onFit: () => fitRef.current(),
    onSave: saveDeliberately,
    onHelp: () => setHelpOpen((open) => !open),
    onPanel: (which) => openRail(which === 'templates' ? 'projects' : which),
    onImage: () => picker.current?.click(),
    onCode: () => setSide('elements'),
    onDraw: draw,
  });

  /* --- what a customer sees ------------------------------------ */

  // Only failure takes the whole window. While the design is on its way the
  // chrome is drawn anyway: none of it depends on the document, and a customer
  // who sees the editor appear immediately is not waiting on a round trip they
  // have no way to understand.
  if (status === 'failed') {
    return (
      <div className="tb-boot">
        <div className="tb-boot__inner">
          <p className="tb-boot__title">We could not open this design.</p>
          <p>{problem}</p>
          <p>
            <a href={config.returnUrl}>Go back to the product</a>
          </p>
        </div>
      </div>
    );
  }

  const blocked = doc ? check(doc).some((finding) => finding.level === 'stop') : true;

  const close = () => setSide(null);

  return (
    <div
      className={`tb-app${side ? ' tb-app--side' : ''}${splitScreen ? ' tb-app--three' : ''}`}
      data-mode={mode}
    >
      <TopBar
        product={config.product.name}
        chose={config.product.chose}
        shop={config.shop}
        opening={status === 'opening' || moving}
        saving={saving || uploading}
        savedAt={savedAt}
        canOrder={!blocked}
        guest={!signedIn()}
        template={config.template}
        onImport={config.template ? () => setImporting(true) : undefined}
        onSignIn={() => setAsking('save')}
        onProblem={setProblem}
        onSave={saveDeliberately}
        onDone={() => void finishTemplate()}
        onOpen={(which) => (which === 'projects' ? openRail('projects') : openPanel(which))}
        onHelp={() => setHelpOpen(true)}
        onProof={() => void downloadProof()}
        onThree={config.three ? () => setThree((open) => !open) : undefined}
        threeOpen={three}
        previewInRoom={!!config.three?.rooms?.length}
        inBasket={!!config.inBasket}
        returnUrl={config.returnUrl}
        onRename={(title) => {
          const state = useEditor.getState();

          if (!state.design || !state.doc) return;

          useEditor.setState({ design: { ...state.design, title } });

          // A visitor's title goes into their browser with the design, until
          // the design is the shop's - after which the shop is where it lives,
          // for a guest exactly as for a customer.
          if (!state.design.uuid) keepLocally();
          else {
            void api.rename(state.design.uuid, title, state.doc).then((updated) => {
              useEditor.setState({ design: { ...updated, document: useEditor.getState().doc ?? state.doc! } });
            });
          }
        }}
        onAddToBasket={() => void addToBasket()}
      />

      <ToolRail open={side} onOpen={openRail} />

      {/* The rail's panels. */}
      {side === 'design' && (
        <DesignPanel
          found={offers}
          onClose={close}
          onPick={(document) => useEditor.getState().commit('Use a template', () => document)}
        />
      )}
      {side === 'elements' && <ElementsPanel onDraw={draw} onClose={close} onProblem={setProblem} />}
      {side === 'text' && <TextPanel onClose={close} />}
      {side === 'uploads' && (
        <UploadsPanel uploading={uploading} onUpload={() => picker.current?.click()} onClose={close} />
      )}
      {side === 'draw' && <DrawPanel mode={mode} onDraw={draw} onClose={close} />}
      {side === 'layers' && <LayersPanel onClose={close} onMenu={setMenu} />}
      {side === 'projects' &&
        (signedIn() ? (
          <DesignsPanel onClose={close} onTemplates={() => setTemplates('asked')} />
        ) : (
          <SignedOutDesigns onSignIn={() => setAsking('designs')} onClose={close} />
        ))}

      {/* The panels the floating toolbar opens, in the same column. */}
      {side === 'effects' && <EffectsPanel onClose={close} />}
      {side === 'position' && <PositionPanel onClose={close} />}
      {side === 'colour' && <ColourPanel target={colourFor} onClose={close} />}
      {side === 'font' && <FontPanel onClose={close} />}
      {side === 'settings' && <SettingsPanel onClose={close} />}
      {side === 'mask' && <MaskPanel onClose={close} />}

      {importing && <ImportDialog onClose={() => setImporting(false)} />}

      {templates && (
        <Templates
          found={offers}
          greeting={templates === 'greeting'}
          onClose={() => setTemplates(null)}
          onPick={(document) => {
            /*
             * The template replaces the document but not the design: the
             * customer keeps the same record, the same uuid and the same place
             * in their list, and can undo straight back to the blank card.
             */
            useEditor.getState().commit('Use a template', () => document);
            setTemplates(null);
          }}
        />
      )}

      <CanvasView
        onFitReady={captureFit}
        onThumbReady={captureThumb}
        onMenu={setMenu}
        drawing={mode === 'select' ? null : mode}
        onDrawn={() => setMode('select')}
      >
        <ContextBar panel={toolbarPanel} onPanel={openPanel} />
        <PageStrip />
        <CanvasFoot onFit={() => fitRef.current()} onHelp={() => setHelpOpen(true)} />
      </CanvasView>

      {/* 3D preview: only for products whose 3D is switched on and complete. */}
      {three && config.three && <ThreeView config={config.three} onClose={() => setThree(false)} />}

      <PreflightBar />

      {menu && <LayerMenu at={menu} onClose={() => setMenu(null)} onPanel={(which) => openPanel(which)} />}

      <input
        ref={picker}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        hidden
        onChange={async (event) => {
          const chosen = Array.from(event.target.files ?? []);

          // Cleared straight away, so choosing the same file twice in a row
          // still fires a change event the second time.
          event.target.value = '';

          for (const file of chosen) {
            setUploading(true);

            const trouble = await addImage(file);

            setUploading(false);

            if (trouble) {
              setProblem(trouble);
              break;
            }
          }
        }}
      />

      {problem && (
        <div className="tb-toast" role="alert">
          <span>{problem}</span>
          <button type="button" className="tb-btn tb-btn--quiet" onClick={() => setProblem('')}>
            Close
          </button>
        </div>
      )}

      {notice && (
        <div className="tb-toast tb-toast--good" role="status">
          <span>{notice}</span>
          <button type="button" className="tb-btn tb-btn--quiet" onClick={() => setNotice('')}>
            Close
          </button>
        </div>
      )}

      {leftover && !asking && <LeftoverNotice busy={moving} onKeep={() => void keepLeftover()} onDiscard={discardLeftover} />}

      {checking && config.confirm && (
        <CheckDialog
          text={config.confirm.text}
          label={config.confirm.label}
          onConfirm={() => {
            setChecking(false);
            void addToBasket(true);
          }}
          onClose={() => setChecking(false)}
        />
      )}

      {asking && <SignInDialog reason={asking} onClose={() => setAsking(null)} />}

      {helpOpen && <Shortcuts onClose={() => setHelpOpen(false)} />}

      {/* Last, and above everything: it covers the confirm box that opened it. */}
      {sending && <BasketProgress step={sending} updating={!!config.inBasket} />}
    </div>
  );
}

/**
 * Whether a design has anything on it yet.
 *
 * A page's own background counts: a shop template that is a coloured card with
 * nothing on it is still someone's decision, and replacing it silently would
 * be wrong.
 */
function isBlank(doc: TrimboxDocument): boolean {
  return doc.pages.every((page) => page.layers.length === 0 && !page.background);
}

/** The face a brand new text layer will want, so it is ready before it is asked for. */
function defaultFace() {
  return { family: firstFamily(), weight: 400, style: 'normal' as const };
}

/**
 * The faces a document's own text layers ask for.
 *
 * A design records the fonts it uses, but a brand new one has not been saved
 * yet and its list is empty - so this reads the layers instead. Anything else
 * the customer picks later is fetched when they pick it.
 */
function firstFaces( doc: TrimboxDocument ) {
  const wanted = new Map<string, { family: string; weight: number; style: 'normal' | 'italic' }>();

  for ( const page of doc.pages ) {
    for ( const layer of flatten( page.layers ) ) {
      if ( layer.type !== 'text' ) continue;

      const face = {
        family: layer.font.family,
        weight: layer.font.weight,
        style: layer.font.style,
      };

      wanted.set( `${ face.family }|${ face.weight }|${ face.style }`, face );
    }
  }

  return Array.from( wanted.values() );
}

